import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import ptBrLocale from "@fullcalendar/core/locales/pt-br";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import EventAvailableRoundedIcon from "@mui/icons-material/EventAvailableRounded";
import EventBusyRoundedIcon from "@mui/icons-material/EventBusyRounded";
import FileDownloadRoundedIcon from "@mui/icons-material/FileDownloadRounded";
import ListAltRoundedIcon from "@mui/icons-material/ListAltRounded";
import LaunchRoundedIcon from "@mui/icons-material/LaunchRounded";
import PlaceRoundedIcon from "@mui/icons-material/PlaceRounded";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import { ApiCMSRequest } from "data/ApiCMS";
import { ListProps } from "./types";
import useEducationalUnits from "./hooks/useEducationalUnits";
import { canViewEvent, eventViewButtonSx, getEventViewUrl } from "./eventView";

const DASHBOARD_EVENTS_QUERY = `
  query EventsDashboard($perPage: Int, $status: StatusEnum) {
    Events(perPage: $perPage, status: $status) {
      items {
        id
        title
        slug
        status
        registration
        registrationLink
        address
        formId
        publishOn
        expiresIn
        dateInit
        dateFinish
        hourInit
        educationalUnitId
        localization
        modality
      }
    }
  }
`;

type EventStatus = "ACTIVE" | "DRAFT" | "INACTIVE";
type EventModality = "ONLINE" | "PRESENCIAL" | "HIBRIDA";

interface EventDashboardItem {
  id: number;
  title: string;
  slug: string;
  status: EventStatus;
  registration?: string;
  registrationLink?: string;
  address?: string;
  formId?: string;
  publishOn?: string;
  expiresIn?: string;
  dateInit: string;
  dateFinish: string;
  hourInit?: string;
  educationalUnitId?: number;
  localization?: string;
  modality: EventModality;
}

interface EventsDashboardGql {
  Events: {
    items: EventDashboardItem[];
  };
}

const DASHBOARD_PANEL_HEIGHT = 590;

const UNIT_COLORS = [
  "#0e8161",
  "#4152b4",
  "#c2410c",
  "#9333ea",
  "#be123c",
  "#047857",
  "#0369a1",
  "#b45309",
];

const getUnitColor = (unitId?: number) => {
  if (!unitId) return "#6b7280";
  return UNIT_COLORS[Math.abs(unitId) % UNIT_COLORS.length];
};

const normalizeEventDateTimeValue = (value?: string, fallbackHour?: string) => {
  if (!value) return value;

  const midnightUtcMatch = value.match(/^(\d{4}-\d{2}-\d{2})T00:00:00(?:\.000)?Z$/);
  if (midnightUtcMatch) {
    return `${midnightUtcMatch[1]}T${fallbackHour || "00:00"}:00`;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  const pad = (part: number) => String(part).padStart(2, "0");

  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
};

const formatDateTime = (value?: string) => {
  if (!value) return "-";

  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
};

const getStatusLabel = (status: EventStatus) => {
  if (status === "ACTIVE") return "Ativo";
  if (status === "DRAFT") return "Rascunho";
  return "Inativo";
};

const getStatusColor = (status: EventStatus): "success" | "warning" | "default" => {
  if (status === "ACTIVE") return "success";
  if (status === "DRAFT") return "warning";
  return "default";
};

const getDashboardErrorMessage = (requestError: any) => {
  const apiMessage =
    requestError?.response?.data?.errors?.[0]?.message || requestError?.message || "";
  const lowerMessage = String(apiMessage).toLowerCase();

  if (lowerMessage.includes("network error") || lowerMessage.includes("unable to connect")) {
    return "Não foi possível conectar com a API de eventos. Verifique se o CMS está rodando.";
  }

  if (lowerMessage.includes("prisma") || lowerMessage.includes("internal server error")) {
    return "Não foi possível carregar os eventos agora. Tente novamente em alguns instantes.";
  }

  return apiMessage || "Não foi possível carregar os eventos.";
};

function StatCard({ title, value, icon }: { title: string; value: number; icon: JSX.Element }) {
  return (
    <Card sx={{ height: "100%", borderRadius: 2 }}>
      <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1.5}>
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={700}>
              {title}
            </Typography>
            <Typography variant="h5" fontWeight={700} lineHeight={1.1}>
              {value}
            </Typography>
          </Box>
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: 1.5,
              display: "grid",
              placeItems: "center",
              color: "#0e8161",
              backgroundColor: alpha("#0e8161", 0.12),
              "& svg": { fontSize: 20 },
            }}
          >
            {icon}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

function Dashboard({ path }: ListProps) {
  const navigate = useNavigate();
  const calendarRef = useRef<FullCalendar | null>(null);
  const { units, unitsById } = useEducationalUnits();
  const [events, setEvents] = useState<EventDashboardItem[]>([]);
  const [selectedUnitId, setSelectedUnitId] = useState<number | null>(null);
  const [dateStartFilter, setDateStartFilter] = useState("");
  const [dateEndFilter, setDateEndFilter] = useState("");
  const [selectedEvent, setSelectedEvent] = useState<EventDashboardItem | null>(null);
  const [upcomingOpen, setUpcomingOpen] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();

  useEffect(() => {
    let ignore = false;

    setLoading(true);
    setError(undefined);

    ApiCMSRequest<EventsDashboardGql>({
      query: DASHBOARD_EVENTS_QUERY,
      variables: { perPage: -1, status: null },
    })
      .then((response) => {
        if (ignore) return;

        const items = response.data.data?.Events.items;
        if (!items) throw new Error("Não foi possível carregar os eventos.");

        setEvents(
          items.map((event) => ({
            ...event,
            dateInit: normalizeEventDateTimeValue(event.dateInit, event.hourInit) || event.dateInit,
            dateFinish: normalizeEventDateTimeValue(event.dateFinish, "23:59") || event.dateFinish,
          }))
        );
      })
      .catch((requestError) => {
        if (ignore) return;
        setError(getDashboardErrorMessage(requestError));
      })
      .finally(() => {
        if (!ignore) setLoading(false);
      });

    return () => {
      ignore = true;
    };
  }, []);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      calendarRef.current?.getApi().updateSize();
    }, 220);

    return () => window.clearTimeout(timeout);
  }, [upcomingOpen]);

  const dashboardData = useMemo(() => {
    const now = new Date();
    const unitFilteredEvents = selectedUnitId
      ? events.filter((event) => event.educationalUnitId === selectedUnitId)
      : events;
    const startFilter = dateStartFilter ? new Date(`${dateStartFilter}T00:00:00`) : null;
    const endFilter = dateEndFilter ? new Date(`${dateEndFilter}T23:59:59`) : null;
    const filteredEvents = unitFilteredEvents.filter((event) => {
      const eventStart = new Date(event.dateInit);
      const eventEnd = new Date(event.dateFinish || event.dateInit);

      if (startFilter && eventEnd < startFilter) return false;
      if (endFilter && eventStart > endFilter) return false;

      return true;
    });
    const activeEvents = filteredEvents.filter((event) => event.status === "ACTIVE");
    const futureEvents = activeEvents
      .filter((event) => new Date(event.dateFinish || event.dateInit) >= now)
      .sort((a, b) => new Date(a.dateInit).getTime() - new Date(b.dateInit).getTime());
    const pastEvents = activeEvents.filter(
      (event) => new Date(event.dateFinish || event.dateInit) < now
    );
    const draftEvents = filteredEvents.filter((event) => event.status === "DRAFT");
    const unitEventCounts = events.reduce<Record<number, number>>((acc, event) => {
      if (!event.educationalUnitId) return acc;
      acc[event.educationalUnitId] = (acc[event.educationalUnitId] || 0) + 1;
      return acc;
    }, {});

    return {
      activeEvents,
      filteredEvents,
      futureEvents,
      pastEvents,
      draftEvents,
      unitEventCounts,
      calendarEvents: activeEvents.map((event) => ({
        id: String(event.id),
        title: event.title,
        start: event.dateInit,
        end: event.dateFinish,
        backgroundColor: getUnitColor(event.educationalUnitId),
        borderColor: getUnitColor(event.educationalUnitId),
        extendedProps: {
          unitName: event.educationalUnitId
            ? unitsById[event.educationalUnitId]?.visibleName ||
              `Unidade ${event.educationalUnitId}`
            : "Sem unidade",
        },
      })),
    };
  }, [dateEndFilter, dateStartFilter, events, selectedUnitId, unitsById]);

  const getEventUnitName = (event: EventDashboardItem) => {
    if (event.educationalUnitId) {
      return (
        unitsById[event.educationalUnitId]?.visibleName || `Unidade ${event.educationalUnitId}`
      );
    }

    return event.localization || "Sem unidade";
  };

  const handleOpenEventSummary = (eventId: string | number) => {
    const event = events.find((item) => item.id === Number(eventId));
    if (event) setSelectedEvent(event);
  };

  const handleEditSelectedEvent = () => {
    if (!selectedEvent) return;
    navigate(`${path}/eventos/${selectedEvent.id}`);
  };

  const handleExportXlsx = () => {
    const rows = dashboardData.filteredEvents.map((event) => ({
      ID: event.id,
      Titulo: event.title,
      Slug: event.slug,
      Status: getStatusLabel(event.status),
      Unidade: getEventUnitName(event),
      "ID Unidade": event.educationalUnitId || "",
      Modalidade: event.modality,
      Localizacao: event.localization || "",
      Endereco: event.address || "",
      "Início do evento": event.dateInit,
      "Fim do evento": event.dateFinish,
      Inscrição: event.registration || "",
      "Link de inscrição": event.registrationLink || "",
      "Formulario ID": event.formId || "",
      "Publicar em": event.publishOn || "",
      "Expira em": event.expiresIn || "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Eventos");
    XLSX.writeFile(workbook, "eventos-uniftec.xlsx");
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <Stack spacing={2} mt={2}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", md: "center" }}
          spacing={1.5}
        >
          <Typography variant="h4" fontWeight={700}>
            Dashboard de eventos
          </Typography>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <Button
              variant="outlined"
              color="info"
              startIcon={<ListAltRoundedIcon />}
              onClick={() => navigate(`${path}/eventos/lista`)}
            >
              Eventos cadastrados
            </Button>
            <Button
              variant="contained"
              color="info"
              startIcon={<AddRoundedIcon />}
              onClick={() => navigate(`${path}/eventos/new`)}
            >
              Novo evento
            </Button>
            <Button
              variant="contained"
              color="success"
              startIcon={<FileDownloadRoundedIcon />}
              onClick={handleExportXlsx}
            >
              Exportar XLSX
            </Button>
          </Stack>
        </Stack>

        {error && <Alert severity="error">{error}</Alert>}

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, minmax(0, 1fr))",
              lg: "repeat(4, minmax(0, 1fr))",
            },
            gap: 2,
          }}
        >
          <Box>
            <StatCard
              title="Proximos eventos"
              value={dashboardData.futureEvents.length}
              icon={<EventAvailableRoundedIcon />}
            />
          </Box>
          <Box>
            <StatCard
              title="Ativos"
              value={dashboardData.activeEvents.length}
              icon={<CalendarMonthRoundedIcon />}
            />
          </Box>
          <Box>
            <StatCard
              title="Rascunhos"
              value={dashboardData.draftEvents.length}
              icon={<ListAltRoundedIcon />}
            />
          </Box>
          <Box>
            <StatCard
              title="Encerrados"
              value={dashboardData.pastEvents.length}
              icon={<EventBusyRoundedIcon />}
            />
          </Box>
        </Box>

        <Card sx={{ borderRadius: 2 }}>
          <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
            <Stack spacing={1.75}>
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={1.25}
                alignItems={{ md: "center" }}
              >
                <Typography variant="button" color="text.secondary" sx={{ minWidth: 76 }}>
                  Unidade
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip
                    label="Todas"
                    onClick={() => setSelectedUnitId(null)}
                    variant={selectedUnitId === null ? "filled" : "outlined"}
                    sx={{
                      borderColor: "#6b7280",
                      backgroundColor:
                        selectedUnitId === null ? alpha("#6b7280", 0.14) : "transparent",
                    }}
                  />
                  {units
                    .filter((unit) => dashboardData.unitEventCounts[unit.id])
                    .map((unit) => {
                      const color = getUnitColor(unit.id);
                      return (
                        <Chip
                          key={unit.id}
                          label={`${unit.visibleName} (${dashboardData.unitEventCounts[unit.id]})`}
                          onClick={() => setSelectedUnitId(unit.id)}
                          variant={selectedUnitId === unit.id ? "filled" : "outlined"}
                          sx={{
                            borderColor: color,
                            backgroundColor:
                              selectedUnitId === unit.id ? alpha(color, 0.14) : "transparent",
                            "&:before": {
                              content: '""',
                              width: 10,
                              height: 10,
                              borderRadius: "50%",
                              backgroundColor: color,
                              ml: 1,
                            },
                          }}
                        />
                      );
                    })}
                </Stack>
              </Stack>

              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={1.25}
                alignItems={{ md: "center" }}
              >
                <Typography variant="button" color="text.secondary" sx={{ minWidth: 76 }}>
                  Período
                </Typography>
                <TextField
                  size="small"
                  type="date"
                  label="Início"
                  value={dateStartFilter}
                  onChange={(event) => setDateStartFilter(event.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ width: { xs: "100%", sm: 210 } }}
                />
                <TextField
                  size="small"
                  type="date"
                  label="Fim"
                  value={dateEndFilter}
                  onChange={(event) => setDateEndFilter(event.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ width: { xs: "100%", sm: 210 } }}
                />
                {(dateStartFilter || dateEndFilter || selectedUnitId) && (
                  <Button
                    variant="text"
                    color="secondary"
                    sx={{ px: 1.5 }}
                    onClick={() => {
                      setSelectedUnitId(null);
                      setDateStartFilter("");
                      setDateEndFilter("");
                    }}
                  >
                    Limpar filtros
                  </Button>
                )}
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        <Box
          sx={{
            display: { xs: "flex", lg: "grid" },
            flexDirection: "column",
            gridTemplateColumns: {
              lg: upcomingOpen ? "minmax(0, 1fr) 340px" : "minmax(0, 1fr) 64px",
            },
            gap: 2,
            alignItems: "stretch",
            transition: "grid-template-columns 180ms ease",
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Card sx={{ height: { lg: DASHBOARD_PANEL_HEIGHT }, borderRadius: 2 }}>
              <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                {loading ? (
                  <Box
                    sx={{
                      height: DASHBOARD_PANEL_HEIGHT - 30,
                      display: "grid",
                      placeItems: "center",
                    }}
                  >
                    <CircularProgress color="info" />
                  </Box>
                ) : (
                  <Box
                    sx={{
                      ".fc": { fontFamily: "inherit" },
                      ".fc .fc-toolbar": { flexWrap: "wrap", gap: 1 },
                      ".fc .fc-toolbar-title": {
                        fontSize: "1.05rem",
                        fontWeight: 700,
                        color: "#1f3f68",
                      },
                      ".fc .fc-button": {
                        borderRadius: "6px",
                        border: "0",
                        backgroundColor: "#4152b4",
                        padding: "0.42rem 0.68rem",
                        textTransform: "capitalize",
                        boxShadow: "none",
                      },
                      ".fc .fc-button:disabled": { backgroundColor: "#9ca3af" },
                      ".fc .fc-col-header-cell": { py: 0.5 },
                      ".fc .fc-daygrid-day-frame": { minHeight: 58 },
                      ".fc .fc-daygrid-day-number": { fontSize: "0.85rem", padding: "3px 5px" },
                      ".fc .fc-daygrid-event": {
                        borderRadius: "4px",
                        padding: "0 4px",
                        fontSize: "0.72rem",
                      },
                    }}
                  >
                    <FullCalendar
                      ref={calendarRef}
                      plugins={[dayGridPlugin, interactionPlugin]}
                      initialView="dayGridMonth"
                      locale={ptBrLocale}
                      height={DASHBOARD_PANEL_HEIGHT - 30}
                      headerToolbar={{
                        left: "title",
                        center: "",
                        right: "today prev,next",
                      }}
                      events={dashboardData.calendarEvents}
                      displayEventTime
                      eventTimeFormat={{
                        hour: "2-digit",
                        minute: "2-digit",
                        meridiem: false,
                      }}
                      eventClick={(info) => handleOpenEventSummary(info.event.id)}
                    />
                  </Box>
                )}
              </CardContent>
            </Card>
          </Box>

          <Box sx={{ width: { xs: "100%", lg: upcomingOpen ? 340 : 64 }, minWidth: 0 }}>
            <Card
              sx={{
                borderRadius: 2,
                overflow: "hidden",
                height: { lg: DASHBOARD_PANEL_HEIGHT },
                transition: "width 180ms ease",
              }}
            >
              <CardContent
                sx={{
                  height: "100%",
                  p: upcomingOpen ? 1.5 : 1,
                  "&:last-child": { pb: upcomingOpen ? 1.5 : 1 },
                }}
              >
                {!upcomingOpen ? (
                  <Stack
                    direction={{ xs: "row", lg: "column" }}
                    alignItems="center"
                    justifyContent={{ xs: "space-between", lg: "flex-start" }}
                    spacing={1}
                    sx={{ height: "100%" }}
                  >
                    <Tooltip title="Expandir proximos eventos">
                      <IconButton size="small" onClick={() => setUpcomingOpen(true)}>
                        <ChevronLeftRoundedIcon />
                      </IconButton>
                    </Tooltip>
                    <Typography
                      variant="button"
                      color="text.secondary"
                      sx={{
                        display: { xs: "block", lg: "none" },
                        flex: 1,
                      }}
                    >
                      Proximos eventos
                    </Typography>
                    <Typography
                      variant="button"
                      color="text.secondary"
                      sx={{
                        display: { xs: "none", lg: "block" },
                        writingMode: "vertical-rl",
                        textOrientation: "mixed",
                        whiteSpace: "nowrap",
                        mt: 1,
                      }}
                    >
                      Proximos eventos
                    </Typography>
                    <Box
                      sx={{
                        width: 34,
                        height: 34,
                        borderRadius: 1.5,
                        display: "grid",
                        placeItems: "center",
                        color: "#0e8161",
                        backgroundColor: alpha("#0e8161", 0.12),
                      }}
                    >
                      <Typography variant="button">{dashboardData.futureEvents.length}</Typography>
                    </Box>
                  </Stack>
                ) : (
                  <>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      spacing={1}
                    >
                      <Typography variant="h6" fontWeight={700}>
                        Proximos eventos
                      </Typography>
                      <Tooltip title="Recolher para lateral">
                        <IconButton size="small" onClick={() => setUpcomingOpen(false)}>
                          <ChevronRightRoundedIcon />
                        </IconButton>
                      </Tooltip>
                    </Stack>

                    {loading ? (
                      <Box sx={{ py: 4, display: "grid", placeItems: "center" }}>
                        <CircularProgress color="info" size={24} />
                      </Box>
                    ) : dashboardData.futureEvents.length === 0 ? (
                      <Alert severity="info" sx={{ mt: 1.25 }}>
                        Nenhum evento futuro cadastrado.
                      </Alert>
                    ) : (
                      <Stack
                        spacing={0.75}
                        mt={1.25}
                        sx={{
                          maxHeight: { lg: DASHBOARD_PANEL_HEIGHT - 96 },
                          overflowY: "auto",
                          pr: 0.5,
                        }}
                      >
                        {dashboardData.futureEvents.map((event) => {
                          const unitColor = getUnitColor(event.educationalUnitId);
                          const unitName = event.educationalUnitId
                            ? unitsById[event.educationalUnitId]?.visibleName ||
                              `Unidade ${event.educationalUnitId}`
                            : event.localization || event.modality;

                          return (
                            <Box
                              key={event.id}
                              onClick={() => setSelectedEvent(event)}
                              sx={{
                                px: 1.25,
                                py: 1,
                                cursor: "pointer",
                                borderRadius: 1.5,
                                border: `1px solid ${alpha("#1f2937", 0.08)}`,
                                borderLeft: `4px solid ${unitColor}`,
                                "&:hover": { backgroundColor: alpha(unitColor, 0.05) },
                              }}
                            >
                              <Typography
                                variant="button"
                                color="#111827"
                                sx={{ lineHeight: 1.25 }}
                              >
                                {event.title}
                              </Typography>

                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                                mt={0.5}
                                flexWrap="wrap"
                                useFlexGap
                              >
                                <Typography variant="caption" color="text.secondary">
                                  {formatDateTime(event.dateInit)}
                                </Typography>
                                <Stack direction="row" spacing={0.5} alignItems="center">
                                  <PlaceRoundedIcon
                                    sx={{ fontSize: 14, color: "text.secondary" }}
                                  />
                                  <Typography variant="caption" color="text.secondary">
                                    {unitName}
                                  </Typography>
                                </Stack>
                              </Stack>
                            </Box>
                          );
                        })}
                      </Stack>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </Box>
        </Box>
      </Stack>

      <Dialog
        open={!!selectedEvent}
        onClose={() => setSelectedEvent(null)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        {selectedEvent && (
          <>
            <DialogTitle sx={{ pb: 1.25 }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="flex-start"
                spacing={2}
              >
                <Box>
                  <Typography variant="h5" fontWeight={700}>
                    {selectedEvent.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {selectedEvent.slug}
                  </Typography>
                </Box>
                <Chip
                  size="small"
                  label={getStatusLabel(selectedEvent.status)}
                  color={getStatusColor(selectedEvent.status)}
                />
              </Stack>
            </DialogTitle>

            <DialogContent sx={{ pt: 0 }}>
              <Stack spacing={1.5}>
                <Box
                  sx={{
                    height: 4,
                    width: "100%",
                    borderRadius: 999,
                    backgroundColor: getUnitColor(selectedEvent.educationalUnitId),
                  }}
                />

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
                    gap: 1.25,
                  }}
                >
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Início do evento
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {formatDateTime(selectedEvent.dateInit)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Fim do evento
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {formatDateTime(selectedEvent.dateFinish)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Unidade
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {getEventUnitName(selectedEvent)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Modalidade
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {selectedEvent.modality}
                    </Typography>
                  </Box>
                </Box>

                {selectedEvent.localization && (
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <PlaceRoundedIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                    <Typography variant="body2" color="text.secondary">
                      {selectedEvent.localization}
                    </Typography>
                  </Stack>
                )}
              </Stack>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2.5 }}>
              <Button color="secondary" onClick={() => setSelectedEvent(null)}>
                Fechar
              </Button>
              {(() => {
                const disabled = !canViewEvent(selectedEvent.status, selectedEvent.slug);
                const href = disabled
                  ? undefined
                  : getEventViewUrl(
                      selectedEvent.status,
                      selectedEvent.slug,
                      selectedEvent.publishOn
                    );

                return (
                  <Button
                    variant="outlined"
                    color="info"
                    startIcon={<LaunchRoundedIcon />}
                    href={href}
                    target="_blank"
                    rel="noreferrer"
                    disabled={disabled}
                    sx={eventViewButtonSx}
                  >
                    Ver
                  </Button>
                );
              })()}
              <Button
                variant="contained"
                color="info"
                startIcon={<EditRoundedIcon />}
                onClick={handleEditSelectedEvent}
              >
                Editar evento
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}

export default Dashboard;
